<template>
  <div ref="positionRef" :key="keyRender" :style="{ height: `100%` }">
    <div v-if="isRhelCoreOs || (isCntUnit && isLayerSeven)">
      <span>N/A</span>
    </div>
    <div class="scpm-show-chart" v-else-if="metricValue">
      <div class="scpm-energebar" v-if="isPercentUnit">
        <div class="scpm-energebar__first">
          <div
            class="energebar"
            :class="{ 'cursor-default': item.checkDisabled }"
            _comment="energebarIcon"
            :style="{ width: _width }"
            data-target="cell"
          >
            <div
              class="energebar__child"
              :style="{
                height: _height,
                background: _barColor,
                width: percent + '%'
              }"
            ></div>
          </div>
        </div>
        <div class="scpm-energebar__second">{{ percent }}</div>
      </div>
      <div v-else class="scpm-no-bar">
        <div style="display: inline-block; max-width: 100%">
          <div style="display: inline-block">{{ formatValueWithUnit }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, getCurrentInstance, ref } from 'vue';
import legoEventBus from '@/lego-component/utils/eventBus/eventBus';
import { useStore } from 'vuex';

const props = defineProps({
  unit: {
    type: String,
    default: ''
  },
  value: {
    type: String,
    default: ''
  },
  decimal: {
    type: Number,
    default: 2
  },
  item: {
    type: Object,
    default: {}
  },
  header: {
    type: String,
    default: ''
  },
  keyRender: {
    type: String,
    default: ''
  }
});

const metric = computed(
  () =>
    props.item['metrics'] &&
    props.item['metrics'].find((metric) => metric.metricName === props.header)
);

const metricValue = computed(() => {
  return metric.value?.value ?? '';
});

const _barColor = ref('');
const _width = ref('32px');
const _height = ref('8px');
const showChart = ref(false);
const positionRef = ref(null);
const count = ref(0);
let percent = Number(parseFloat(metricValue.value).toFixed(props.decimal));

const { $util, $const } = getCurrentInstance().appContext.config.globalProperties;
const $store = useStore();
if (90 <= percent) {
  _barColor.value = '#fb4d58';
} else if (80 <= percent) {
  _barColor.value = '#f47d23';
} else {
  _barColor.value = '#23d89e';
}
const isPercentUnit = computed(() => props.unit.includes('%'));
const isCntUnit = computed(() => props.unit.includes('cnt'));
const isRhelCoreOs = computed(() => $util.isRhelCoreOs(props.item['osInfo']));
const isLayerSeven = computed(() => $util.isLayerSeven(props.item['layerType']));

const formatValueWithUnit = computed(() => {
  const { value, unit, decimal } = props;
  const numValue = Number(value);
  if (isNaN(numValue)) return `${numValue} ${unit}`;
  const isAbleToConvert = $util.isUnitAbleToConvert(unit);
  const { value: newValue, unit: newUnit } = isAbleToConvert
    ? $util.convertToHigherUnit(numValue, unit)
    : $util.convertNumberSymbol(numValue, unit);
  const roundedValue = Number(newValue.toFixed(decimal));
  const displayValue = $util.formatNumberInThousands(roundedValue);
  if (isAbleToConvert) return `${displayValue} ${newUnit}`;
  return `${displayValue}${newUnit} ${unit}`;
});
const customClick = () => {
  if (props.item.checkDisabled) return;
  legoEventBus.$emit('click-outside', props.keyRender);
  checkAuthorization();
};
const checkAuthorization = () => {
  if (showChart.value) {
    showChart.value = false;
    return;
  }

  const listDataCheckAuth = [
    { productResourceId: props.item.productId, productName: props.item.productName }
  ];

  const payload = {
    productTypeCode: props.item.productTypeCode,
    params: {
      productId: props.item.productId
    },
    listDataCheckAuth,
    loading: false
  };
  $store.dispatch('authStore/checkProductDetail', payload).then((res) => {
    if (res && res.type === $const.AuthorizationResult.ALLOW) {
      showChart.value = true;
    } else {
      const item = {
        productResourceId: props.item['productResourceId'],
        productName: props.item['productName'],
        productId: props.item.productId
      };
      legoEventBus.$emit('reset-selected', item);
    }
  });
};
</script>

<style scoped lang="scss">
.scpm-energebar {
  display: flex;
  gap: 4px;
  height: 28px;
  align-items: center;

  .scpm-energebar__first {
    flex: 1;
    text-align: right;
    .cursor-default {
      cursor: default !important;
    }
    .energebar {
      position: relative;
      display: inline-block;
      height: 8px;
      padding: 0;
      background: rgba(237, 237, 239, 0.95);
      border: 0;
      border-radius: 4px;
      overflow: hidden;
      cursor: pointer;

      .energebar__child {
        position: absolute;
        top: 0;
        left: 0;
        transition: width 0.5s;
        pointer-events: none;
      }
    }
  }

  .scpm-energebar__second {
    flex: 1;
    text-align: left;
    align-self: center;
  }
}
</style>
